import { t } from "@/lib/i18n/interpolate";
import type { DocumentModel } from "@/lib/tool-kit/document";
import {
  countWords,
  lowerFirstWord,
  paragraphsOrPrompt,
  splitLines,
} from "@/lib/tool-kit/essay-templates/text";
import { paragraphFromParts } from "@/lib/tool-kit/research-writing/specific-aims";

/**
 * A research gap statement and a purpose statement, built around one gap type.
 *
 * The moves follow Swales' "Create a Research Space" model of an article introduction:
 * establish the territory (what is known), establish the niche (the gap, and why it
 * matters), occupy the niche (the purpose of this study). The gap types are the seven
 * Miles (2017) names, drawing on Robinson et al. (2011) and Müller-Bloch and Kranz
 * (2015): evidence, knowledge, practice–knowledge conflict, methodological, empirical,
 * theoretical and population. The categories overlap, and the tool does not decide
 * which one fits; it supplies the connecting words for the one the writer chooses and
 * places the writer's clauses inside them.
 *
 * Whether the gap exists is for the writer's search to show. The checks flag absolute
 * claims ("no study has…") that one missed paper would disprove.
 */

export const GAP_TYPES = [
  "evidence",
  "knowledge",
  "practice",
  "method",
  "empirical",
  "theory",
  "population",
] as const;
export type GapType = (typeof GAP_TYPES)[number];

export interface GapInput {
  gapType: GapType;
  /** A clause: what research has established. */
  territory: string;
  /** The type-specific missing element. */
  detail: string;
  /** A clause: why the gap matters. */
  consequence: string;
  /** A verb phrase: what this study will do. */
  approach: string;
  /** Optional setting or sample, finishing the purpose sentence. */
  setting: string;
  /** How the writer checked that the gap exists. */
  search: string;
}

const TRAILING_STOP = /[.;:\s]+$/;
const KNOWLEDGE_START =
  /^(?:whether|how|why|what|which|who|when|where|if|the extent|the degree)\b/i;
const ABSOLUTE_PATTERN =
  /\b(?:no (?:study|studies|research|literature|work|one)|nobody|never (?:been )?(?:studied|examined|investigated|tested)|nothing is known|the first (?:study|to|research)|has not been (?:studied|researched|examined) at all)\b/gi;

/** One line with no closing punctuation, ready to sit inside a sentence. */
export function clause(text: string): string {
  return splitLines(text).join(" ").replace(TRAILING_STOP, "").trim();
}

function has(text: string): boolean {
  return clause(text) !== "";
}

export interface GapLabels {
  defaultTitle: string;
  subtitle: string;
  headings: { gap: string; purpose: string; checked: string };
  tips: { gap: string; purpose: string; checked: string };
  territoryFrame: string;
  gapFrames: Record<GapType, string>;
  consequenceFrame: string;
  purposeFrame: string;
  prompts: {
    territory: string;
    gap: Record<GapType, string>;
    consequence: string;
    approach: string;
    search: string;
  };
}

/** The gap paragraph's three sentences — territory, gap, consequence — empty where missing. */
export function gapParts(input: GapInput, labels: GapLabels): string[] {
  return [
    has(input.territory)
      ? t(labels.territoryFrame, { territory: clause(input.territory) })
      : "",
    has(input.detail)
      ? t(labels.gapFrames[input.gapType], { detail: clause(input.detail) })
      : "",
    has(input.consequence)
      ? t(labels.consequenceFrame, { consequence: clause(input.consequence) })
      : "",
  ];
}

/** The sentences the writer has supplied, in order. */
export function gapSentences(input: GapInput, labels: GapLabels): string[] {
  return gapParts(input, labels).filter((sentence) => sentence !== "");
}

export function purposeSentence(
  input: GapInput,
  labels: GapLabels,
  locale: string
): string {
  if (!has(input.approach)) {
    return "";
  }
  const approach = lowerFirstWord(clause(input.approach), locale);
  const setting = has(input.setting) ? ` ${clause(input.setting)}` : "";
  return t(labels.purposeFrame, { approach, setting });
}

export function gapWords(
  input: GapInput,
  labels: GapLabels,
  locale: string
): number {
  return countWords(
    [
      ...gapSentences(input, labels),
      purposeSentence(input, labels, locale),
    ].join(" ")
  );
}

export type GapCheckId =
  | "territory"
  | "detail"
  | "consequence"
  | "approach"
  | "search"
  | "start"
  | "absolute";

export interface GapCheck {
  id: GapCheckId;
  state: "done" | "todo" | "note";
  items?: string[];
}

/** Absolute claims in anything the writer wrote about the gap, lower-cased and de-duplicated. */
export function absoluteClaims(input: GapInput): string[] {
  const text = [
    input.territory,
    input.detail,
    input.consequence,
    input.approach,
  ].join("\n");
  const found = (text.match(ABSOLUTE_PATTERN) ?? []).map((m) =>
    m.toLowerCase()
  );
  return [...new Set(found)];
}

export function gapChecks(input: GapInput): GapCheck[] {
  const checks: GapCheck[] = (
    ["territory", "detail", "consequence", "approach", "search"] as const
  ).map((id) => {
    const field = id === "search" ? input.search : input[id];
    if (has(field)) {
      return { id, state: "done" };
    }
    return { id, state: id === "search" ? "note" : "todo" };
  });
  if (input.gapType === "knowledge" && has(input.detail)) {
    checks.push({
      id: "start",
      state: KNOWLEDGE_START.test(clause(input.detail)) ? "done" : "note",
    });
  }
  const claims = absoluteClaims(input);
  checks.push(
    claims.length === 0
      ? { id: "absolute", state: "done" }
      : { id: "absolute", state: "note", items: claims }
  );
  return checks;
}

export function buildGapDocument(
  input: GapInput,
  labels: GapLabels,
  locale: string
): DocumentModel {
  const parts = gapParts(input, labels);
  const gapBlocks = paragraphFromParts([
    { text: parts[0], prompt: labels.prompts.territory },
    { text: parts[1], prompt: labels.prompts.gap[input.gapType] },
    { text: parts[2], prompt: labels.prompts.consequence },
  ]);
  const purpose = purposeSentence(input, labels, locale);
  return {
    title: labels.defaultTitle,
    subtitle: labels.subtitle,
    sections: [
      {
        heading: labels.headings.gap,
        blocks: [{ kind: "guidance", text: labels.tips.gap }, ...gapBlocks],
      },
      {
        heading: labels.headings.purpose,
        blocks: [
          { kind: "guidance", text: labels.tips.purpose },
          purpose
            ? { kind: "paragraph", text: purpose }
            : { kind: "prompt", text: labels.prompts.approach },
        ],
      },
      {
        heading: labels.headings.checked,
        blocks: [
          { kind: "guidance", text: labels.tips.checked },
          ...paragraphsOrPrompt(input.search, labels.prompts.search),
        ],
      },
    ],
  };
}
